import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

function normalizePct(raw: unknown): number {
  if (raw == null) return 0.5;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0.5;
  return n > 1 ? n / 100 : n;
}

/**
 * Returns the resale price percentage as a fraction (0..1) for a given company.
 * When `companyId` is omitted, falls back to the viewer's active company.
 * Reads company_settings.harvest_recommendations.resale_price_pct.
 * Falls back to 0.5 when the setting is missing / invalid (0 remains a valid value).
 */
export function useResalePricePct(companyId?: string | null): number {
  const { id: activeCompanyId } = useCurrentCompany();
  const effectiveId = companyId ?? activeCompanyId ?? null;
  const { data } = useQuery({
    queryKey: ["resale_price_pct", effectiveId],
    enabled: !!effectiveId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_settings")
        .select("setting_value")
        .eq("company_id", effectiveId!)
        .eq("setting_key", "harvest_recommendations")
        .maybeSingle();
      if (error) throw error;
      const raw = (data?.setting_value as { resale_price_pct?: number } | null)?.resale_price_pct;
      return normalizePct(raw);
    },
    staleTime: 60_000,
  });
  return data ?? 0.5;
}

/**
 * Batched variant: returns a Map<companyId, pct> for a list of company IDs.
 * Companies missing the setting fall back to 0.5.
 */
export function useResalePricePctMap(companyIds: string[]): Map<string, number> {
  const uniqueIds = Array.from(new Set(companyIds.filter(Boolean)));
  const key = uniqueIds.slice().sort().join(",");
  const { data } = useQuery({
    queryKey: ["resale_price_pct_map", key],
    enabled: uniqueIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_settings")
        .select("company_id, setting_value")
        .in("company_id", uniqueIds)
        .eq("setting_key", "harvest_recommendations");
      if (error) throw error;
      const map = new Map<string, number>();
      for (const id of uniqueIds) map.set(id, 0.5);
      for (const row of data || []) {
        const raw = (row.setting_value as { resale_price_pct?: number } | null)?.resale_price_pct;
        map.set(row.company_id as string, normalizePct(raw));
      }
      return map;
    },
    staleTime: 60_000,
  });
  return data ?? new Map(uniqueIds.map((id) => [id, 0.5]));
}
